import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Copy, ExternalLink, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";
import { mockApiKeys, mockIntegrations } from "./constants";

export function ApiIntegrationsTab() {
  const [newKeyName, setNewKeyName] = useState("");
  const [apiKeys, setApiKeys] = useState(mockApiKeys);
  const [keyToRevoke, setKeyToRevoke] = useState<{ id: string; name: string } | null>(null);

  const handleGenerate = () => {
    if (!newKeyName.trim()) return;
    toast.success("API Key Generated", { description: `Key "${newKeyName}" has been created.` });
    setNewKeyName("");
  };

  const handleCopy = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.success("Copied", { description: "API key copied to clipboard." });
  };

  const handleRevoke = () => {
    if (!keyToRevoke) return;
    setApiKeys((prev) => prev.map((k) => (k.id === keyToRevoke.id ? { ...k, status: "revoked" } : k)));
    toast.success("API Key Revoked", { description: `"${keyToRevoke.name}" has been revoked.` });
    setKeyToRevoke(null);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">API Keys</CardTitle>
          <CardDescription>Manage API keys for programmatic access</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Input
              placeholder="Key name (e.g. Production)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              className="flex-1 rounded-lg"
            />
            <Button onClick={handleGenerate} size="sm" className="h-9 gap-1.5 rounded-lg">
              <Plus className="h-3.5 w-3.5" />
              Generate Key
            </Button>
          </div>

          {apiKeys.map((apiKey) => (
            <div key={apiKey.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-foreground">{apiKey.name}</p>
                  <Badge
                    variant="secondary"
                    className={`text-xs border-0 capitalize ${
                      apiKey.status === "active"
                        ? "bg-trend-positive/10 text-trend-positive"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {apiKey.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <code className="text-xs text-muted-foreground font-mono">{apiKey.key}</code>
                  <span className="text-xs text-muted-foreground">Created {apiKey.created}</span>
                  <span className="text-xs text-muted-foreground">Last used {apiKey.lastUsed}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleCopy(apiKey.key)}>
                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
                {apiKey.status === "active" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => setKeyToRevoke({ id: apiKey.id, name: apiKey.name })}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Integrations</CardTitle>
          <CardDescription>Connect external services to Signal</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {mockIntegrations.map((integration) => (
            <div key={integration.id} className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/20 transition-colors">
              <div className="flex items-center gap-3">
                <span className="text-xl">{integration.icon}</span>
                <div>
                  <p className="text-sm font-medium text-foreground">{integration.name}</p>
                  <p className="text-xs text-muted-foreground">{integration.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className={`text-xs border-0 capitalize ${
                    integration.status === "connected"
                      ? "bg-trend-positive/10 text-trend-positive"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {integration.status === "connected" && <Check className="h-3 w-3 mr-1" />}
                  {integration.status}
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs rounded-lg bg-card gap-1"
                >
                  {integration.status === "connected" ? (
                    <>
                      <RefreshCw className="h-3 w-3" />
                      Reconnect
                    </>
                  ) : (
                    <>
                      <ExternalLink className="h-3 w-3" />
                      Connect
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Webhooks</CardTitle>
              <CardDescription>Send real-time events to external endpoints</CardDescription>
            </div>
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs rounded-lg bg-card">
              <Plus className="h-3.5 w-3.5" />
              Add Webhook
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="p-4 rounded-lg border border-dashed border-border text-center">
            <p className="text-sm text-muted-foreground">No webhooks configured</p>
            <p className="text-xs text-muted-foreground mt-1">Add a webhook to receive events like rank changes, test results, and landscape completions</p>
          </div>
        </CardContent>
      </Card>

      <ConfirmDeleteDialog
        open={!!keyToRevoke}
        onOpenChange={(open) => { if (!open) setKeyToRevoke(null); }}
        title="Revoke this API key?"
        description={`"${keyToRevoke?.name}" will immediately stop working. Any integrations using it will lose access. This action cannot be undone.`}
        confirmLabel="Revoke"
        onConfirm={handleRevoke}
      />
    </div>
  );
}
